import React, { Dispatch, ReactNode, SetStateAction } from 'react'
import { Modal } from 'antd'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import { ArrowLeft, Check } from 'react-feather'

import { Button, useShowMessage } from '@pepper/ui'
import { editCompanyDetails } from '@pepper/utils/src/api/users/users'
import { JOURNEYSTAGE } from 'container/CreateOrganisationJourney/CreateOrganisationJourney'
import { getFieldComponent } from 'components/FormFields/FormFields'
import { checkEmptyResponse, fields, validationSchema } from './helpers'

import styles from './_.module.css'

type EditOrgForm = Omit<Prefillbiodata, 'cta' | 'name'>
interface Props {
  companyId: string
  setJourneyState: Dispatch<SetStateAction<JOURNEYSTAGE>>
  data: Prefillbiodata
  company: { organisationName: string; organisationWebsite: string }
}

const initialValue: Prefillbiodata = {
  company_bio: '',
  cta: '',
  tone: '',
  usp: '',
  target_audience: '',
  name: '',
}

function EditOrganisationModal(props: Props): JSX.Element {
  const { data, companyId, setJourneyState, company } = props
  const { cta, name, ...orgDetails } = data

  const isEmptyResponse = checkEmptyResponse(orgDetails)

  const showMessage = useShowMessage()

  const handleCancel = () => {
    setJourneyState(JOURNEYSTAGE.CLOSED)
  }

  const goBack = () => {
    setJourneyState(JOURNEYSTAGE.CREATE)
  }

  const handleSubmit = async (
    values: EditOrgForm,
    action: FormikHelpers<EditOrgForm>
  ) => {
    const { status } = await editCompanyDetails({ id: companyId, body: values })
    if (status === 'error') {
      showMessage({ type: 'error', message: 'Something unexpected happened' })
      handleCancel()
      return
    }

    setJourneyState(JOURNEYSTAGE.ORG_DETAILS_SUBMITTED)
  }

  const title = (
    <div className={styles.title}>
      {`${company.organisationName} at `}
      <a
        target="_blank"
        rel="noreferrer"
        href={`${company.organisationWebsite}`}
      >
        {company.organisationWebsite}
      </a>
      {!isEmptyResponse ? (
        <div>
          Please feel free to edit the pre-filled information based on our
          understanding of you.
        </div>
      ) : (
        <div className={styles.error_state}>
          Looks like we couldn’t find your website. If you feel this is an error
          you can go back and change your website URL or can manually input
          information about the company.
        </div>
      )}
    </div>
  )

  return (
    <Formik
      initialValues={orgDetails || initialValue}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {(props: FormikProps<EditOrgForm>): ReactNode => {
        return (
          <Modal
            title={title}
            centered={true}
            visible={true}
            className={styles.modal}
            maskClosable={false}
            onOk={props.submitForm}
            okButtonProps={{
              htmlType: 'submit',
              loading: props.isSubmitting,
            }}
            onCancel={() => {
              props.resetForm()
              handleCancel()
            }}
            width={720}
            footer={[
              <Button
                onClick={goBack}
                key="back"
                type="ghost"
                className={styles.input_button_icon}
                icon={<ArrowLeft size="16px" />}
              >
                Take Me Back
              </Button>,
              <Button
                onClick={() => handleSubmit}
                key="submit"
                type="primary"
                form="editor-form"
                htmlType="submit"
                disabled={props.isSubmitting}
                className={styles.input_button_icon}
                icon={<Check size="16px" />}
              >
                Perfect!
              </Button>,
            ]}
            okText="Perfect"
          >
            <Form id="editor-form">
              {fields.map(field => (
                <div key={field.name} className={styles.field}>
                  {getFieldComponent({ ...field, actions: props })}
                </div>
              ))}
            </Form>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default EditOrganisationModal
